// src/lib/posthog/error-events.ts
//
// What an error may tell PostHog. A boundary sends `error_name`,
// `digest` and a redacted `route`; a window error or unhandled rejection sends
// `error_name` only. The message, stack and filename never leave the browser
// (`capture_exceptions` stays false in init-options.ts).
//
// `capture` is injectable so specs never load posthog-js.

import { capture as captureEvent } from "./client";
import { logClientError } from "./client-log";
import { EVENTS } from "./events";
import { redactUrl } from "./sanitize";
import { readTranslated } from "./translation";

type ErrorEventDeps = { capture: typeof captureEvent };

/** `listenForClientErrors` also reads `translated` itself, at event time. */
type ClientErrorDeps = ErrorEventDeps & { translated: () => boolean | null };

// Resolved at call time, not import time, so a module that mocks `./client`
// without `capture` can still import the gate that imports this file.
const defaultDeps: ErrorEventDeps = {
  capture: (event, props) => captureEvent(event, props),
};

// Exported for error-events.spec.ts only, to assert the default reader is
// `readTranslated` itself (identity), not an equivalent wrapper.
export const defaultClientErrorDeps: ClientErrorDeps = {
  ...defaultDeps,
  translated: readTranslated,
};

/** A stable name for a thrown value that is not an `Error`. */
const NON_ERROR = "NonError";

function nameOf(value: unknown): string {
  return value instanceof Error ? value.name || "Error" : NON_ERROR;
}

function safeCapture(scope: string, action: () => void): void {
  try {
    action();
  } catch (error) {
    logClientError(scope, { name: nameOf(error) });
  }
}

export function errorBoundaryProps(
  error: Error & { digest?: string },
  pathname: string,
  translated: boolean | null,
): { error_name: string; digest: string | null; route: string; translated: boolean | null } {
  const queryIndex = pathname.indexOf("?");
  const path = queryIndex === -1 ? pathname : pathname.slice(0, queryIndex);
  return {
    error_name: nameOf(error),
    digest: error.digest ?? null,
    route: redactUrl(path),
    translated,
  };
}

export function clientErrorProps(
  reason: unknown,
  translated: boolean | null,
): { error_name: string; translated: boolean | null } {
  return { error_name: nameOf(reason), translated };
}

/** Called from a Next error boundary's effect with `window.location.pathname`. */
export function captureErrorBoundary(
  error: Error & { digest?: string },
  pathname: string,
  translated: boolean | null,
  deps: ErrorEventDeps = defaultDeps,
): void {
  safeCapture("error-boundary", () => {
    deps.capture(EVENTS.ERROR_BOUNDARY_SHOWN, errorBoundaryProps(error, pathname, translated));
  });
}

type ErrorEventTarget = {
  addEventListener(type: string, listener: (event: unknown) => void): void;
  removeEventListener(type: string, listener: (event: unknown) => void): void;
};

function field(event: unknown, key: "error" | "reason"): unknown {
  return event !== null && typeof event === "object"
    ? (event as Record<string, unknown>)[key]
    : undefined;
}

/** Registers the `error` / `unhandledrejection` listeners; returns the unsubscribe. */
export function listenForClientErrors(
  target: ErrorEventTarget,
  deps: ClientErrorDeps = defaultClientErrorDeps,
): () => void {
  const onError = (event: unknown): void => {
    safeCapture("client-error", () => {
      deps.capture(EVENTS.CLIENT_ERROR, clientErrorProps(field(event, "error"), deps.translated()));
    });
  };
  const onRejection = (event: unknown): void => {
    safeCapture("client-error", () => {
      deps.capture(EVENTS.CLIENT_ERROR, clientErrorProps(field(event, "reason"), deps.translated()));
    });
  };
  target.addEventListener("error", onError);
  target.addEventListener("unhandledrejection", onRejection);
  return () => {
    target.removeEventListener("error", onError);
    target.removeEventListener("unhandledrejection", onRejection);
  };
}
